import React, { FC } from 'react'
import Heading from '../../atoms/Heading/Heading'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import DynamicSVG from '../../atoms/DynamicSvg/DynamicSvg'
import { getImage } from '../../../app/utlis/imageConstants'

interface AndroidIdeaProjectProps {
    androidData: any
}

const AndroidIdeaProject: FC<AndroidIdeaProjectProps> = ({ androidData }) => {
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { type: 'spring', stiffness: 70, duration: 0.6 }
        }
    }

    return (
        <div>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.6 }}
            >
                <Heading title='TURNING AN IDEA INTO A REALITY' subTitle='The way we develop our projects' />
            </motion.div>

            <div className='flex flex-col gap-6 md:gap-12 pt-6 md:pt-12'>
                {androidData.AndroidIdeaProjectsData.map((projects: any, index: number) => {
                    const img = getImage(projects.image ?? '') ?? ''
                    const isReverse = projects.row === 'row-reverse'

                    return (
                        <motion.div
                            key={index}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: false, amount: 0.2 }}
                            variants={containerVariants}
                        >
                            {/* ── Title ── */}
                            <motion.div
                                className='flex justify-between font-semibold
                                           text-xl sm:text-2xl md:text-3xl lg:text-4xl
                                           p-4 md:p-4 lg:p-8
                                           px-4 md:px-20 lg:px-40'
                                style={{
                                    flexDirection: projects.row ?? 'row',
                                    color: projects.textColor,
                                    borderTop: '1.5px solid transparent',
                                    borderImageSource: `linear-gradient(90deg, rgb(49,49,49) 19%, ${projects.color} 26%, rgb(49,49,49) 46%, rgb(49,49,49) 85%)`,
                                    borderImageSlice: 1,
                                }}
                                variants={itemVariants}
                                whileHover={{ scale: 1.01 }}
                                transition={{ type: 'spring', stiffness: 300 }}
                            >
                                {projects.title}
                            </motion.div>

                            <div className='border-t border-[#313131]' />

                            {/* ── Content ── */}
                            <motion.div
                                className={classNames(
                                    'flex w-full gap-6 md:gap-8',
                                    'flex-col py-6',
                                    'px-4 sm:px-8 md:px-20 xl:px-40',
                                    'md:py-8',
                                    isReverse ? 'md:flex-row-reverse' : 'md:flex-row'
                                )}
                                style={{
                                    borderBottom: '1.5px solid transparent',
                                    borderImageSource: `linear-gradient(90deg, rgb(49,49,49) 51%, ${projects.color} 81%, rgb(49,49,49) 93%, rgb(49,49,49) 85%)`,
                                    borderImageSlice: 1,
                                }}
                                variants={itemVariants}
                            >
                                {/* Subtitle */}
                                <motion.div
                                    className='text-greyQuaternary text-base sm:text-lg
                                               w-full md:w-[40%]
                                               flex-shrink-0'
                                    style={{ color: projects.text }}
                                    initial={{ opacity: 0, x: isReverse ? 50 : -50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.7, delay: 0.2 }}
                                    viewport={{ once: false, amount: 0.5 }}
                                >
                                    {projects.subtitle}
                                </motion.div>

                                {/* Image */}
                                <motion.div
                                    className='flex items-center justify-center
                                               w-full md:w-[60%]
                                               flex-shrink-0'
                                    style={{ justifyContent: projects.justify ?? 'center' }}
                                    initial={{ opacity: 0, x: isReverse ? -20 : 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.7, delay: 0.3 }}
                                    viewport={{ once: false, amount: 0.5 }}
                                >
                                    {/* Fixed sizes per breakpoint so image is always visible */}
                                    <div className='w-[260px] h-[200px]
                                                    sm:w-[340px] sm:h-[260px]
                                                    md:w-[200px] md:h-[200px]
                                                    lg:w-[300px] lg:h-[230px]
                                                    xl:w-[350px] xl:h-[250px]
                                                    2xl:w-[500px] 2xl:h-[360px]
                                                    flex items-center justify-center'>
                                        <DynamicSVG
                                            color='#fff'
                                            size='w-full h-full'
                                        >
                                            {img}
                                        </DynamicSVG>
                                    </div>
                                </motion.div>
                            </motion.div>
                        </motion.div>
                    )
                })}
            </div>
        </div>
    )
}

export default AndroidIdeaProject